/**
 * 🔗 AI link — the web ET follows picks that an AI host (ChatGPT / Claude, through the
 * CherryClipz MCP connector) writes into the live-picks relay.
 *
 * Same relay and same "act only when version rises" rule as the Premiere panel's 📱 phone
 * control (premiere-panel/js/main.js startPhonePoll), so the two behave identically. Pure
 * JS with injectable I/O so it is unit-testable without React or the network.
 *
 *   const link = createAiLinkPoller({ transcriptId, arm, fetchPicks, onPicks, onStatus });
 *   await link.start();   // arms the relay (owner), then polls every 1.5 s
 *   link.stop();          // stops polling and disarms
 */
export const AI_LINK_INTERVAL_MS = 1500;

export const AI_LINK_STATUS = {
  OFF: "off",
  ARMING: "arming",
  WAITING: "waiting",       // armed, nothing received yet
  LIVE: "live",             // at least one pick-set applied
  DISCONNECTED: "disconnected", // relay refused us (401/409) or the network is down
};

export function createAiLinkPoller({
  transcriptId,
  arm,                       // (armed: boolean) => Promise<{version?: number}>
  fetchPicks,                // () => Promise<{armed, version, picks}>
  onPicks,                   // (ids: Array, picks: Array) => void
  onStatus = () => {},       // (status, extra) => void
  followOnly = false,        // share pages: follow the relay without arming (guests can't arm)
  intervalMs = AI_LINK_INTERVAL_MS,
  setIntervalFn = (fn, ms) => setInterval(fn, ms),
  clearIntervalFn = (h) => clearInterval(h),
}) {
  let timer = null;
  let lastVersion = -1;
  let running = false;
  let inFlight = false;
  let failures = 0;

  const tick = async () => {
    if (!running || inFlight) return;
    inFlight = true;
    try {
      const res = await fetchPicks();
      failures = 0;
      if (!running || !res) return;
      if (typeof res.version === "number" && res.version !== lastVersion) {
        lastVersion = res.version;
        const picks = Array.isArray(res.picks) ? res.picks : [];
        // An arm resets the relay to an empty set; don't wipe the user's ticks for that.
        if (picks.length) {
          onPicks(picks.map((p) => (p && typeof p === "object" ? p.id : p)).filter((id) => id != null), picks);
          onStatus(AI_LINK_STATUS.LIVE, { count: picks.length, version: res.version });
        }
      }
    } catch (err) {
      // 401/409 = the relay no longer honours us (re-armed elsewhere, logged out); after a
      // couple of network blips, surface it, but keep polling so it self-heals.
      failures += 1;
      const hard = err && (err.status === 401 || err.status === 409);
      if (hard || failures >= 3) onStatus(AI_LINK_STATUS.DISCONNECTED, { error: err });
    } finally {
      inFlight = false;
    }
  };

  return {
    async start() {
      if (running) return;
      if (!transcriptId) throw new Error("AI link needs a transcript id");
      onStatus(AI_LINK_STATUS.ARMING);
      const res = followOnly ? null : await arm(true);
      // Start BELOW the relay's version so the first tick applies a pick-set an AI placed
      // before the link was turned on (the arm keeps it; see keep_picks in arm_live_picks).
      lastVersion = -1;
      running = true;
      onStatus(AI_LINK_STATUS.WAITING, { version: res && res.version });
      timer = setIntervalFn(tick, intervalMs);
    },
    stop() {
      const was = running;
      running = false;
      if (timer) { clearIntervalFn(timer); timer = null; }
      onStatus(AI_LINK_STATUS.OFF);
      if (was && !followOnly) arm(false).catch(() => {});
    },
    isRunning: () => running,
    _tick: tick, // exposed for tests
  };
}
